import { defineSlotRecipe } from "@chakra-ui/react-v3";
import { radioCardAnatomy } from "@chakra-ui/react-v3/anatomy";

import { focusRing } from "theme/tokens/accessibility";

const $cardBg = "--radio-card-bg";
const $cardStrokeColor = "--radio-card-stroke-color";
const $cardStrokeThickness = "--radio-card-stroke-thickness";
const $cardLabelColor = "--radio-card-label-color";
const $cardDescriptionColor = "--radio-card-description-color";
const $cardIndicatorColor = "--radio-card-indicator-color";

const GRID_GAP = "2";

/**
 * Fit columns to the container rather than viewport breakpoints:
 * - The calc subtracts the gaps and divides the remaining width by the column
 *   limit, so auto-fill can never add more than maxColumns.
 * - max(8rem, ...) keeps cards readable, dropping columns as space runs out.
 * - min(100%, ...) lets a single card fit a container narrower than 8rem.
 * - minmax(..., 1fr) shares leftover space equally. auto-fill preserves empty
 *   tracks, so a short row keeps the same card widths as a full one.
 * GRID_GAP must match the root's gap so the column-limit calculation stays exact.
 */
const gridColumns = (maxColumns: number) =>
  `repeat(auto-fill, minmax(min(100%, max({sizes.32}, calc((100% - ${maxColumns - 1} * {spacing.${GRID_GAP}}) / ${maxColumns}))), 1fr))`;

/**
 * Colors, stroke, radius, and spacing start from the Selectable tile recipe,
 * which Radio card is meant to replace. The card is the `item` (a `label`
 * wrapping the hidden radio input); its content sits in a two-column grid:
 *
 *   label        | addon, or the lock on a locked card
 *   description (full width)
 *
 * The selected card is marked by its background and border colour; there is
 * no check. `inline` puts the label, description, and lock on one line
 * instead, and sizes each card to its content. Inline cards have no addon.
 */
export const radioCardRecipe = defineSlotRecipe({
  slots: radioCardAnatomy.keys(),

  base: {
    root: {
      display: "grid",
      gap: GRID_GAP,
    },

    // Takes a full row above the cards in both the grid and the inline layout.
    label: {
      gridColumn: "1 / -1",
      width: "full",
      color: "fg.emphasized",
    },

    item: {
      position: "relative",
      display: "flex",
      minWidth: "0",
      userSelect: "none",

      bgColor: `var(${$cardBg})`,
      boxShadow: `inset 0 0 0 var(${$cardStrokeThickness}) var(${$cardStrokeColor})`,
      borderRadius: "lg",
      cursor: "pointer",

      _focusVisible: {
        ...focusRing,
      },

      _active: {
        transform: "translateY(1px)",
      },

      _disabled: {
        cursor: "not-allowed",
      },
    },

    itemControl: {
      display: "flex",
      flex: "1",
      alignItems: "center",
      minWidth: "0",
      py: "3",
      px: "4",
      textAlign: "left",
    },

    itemContent: {
      display: "grid",
      flex: "1",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      // The description row keeps at least one line (`mono.12`) on cards
      // without a description, so every card in a group has the same height
      // and the labels line up.
      gridTemplateRows:
        "auto minmax(calc({fontSizes.xs} * {lineHeights.mono}), auto)",
      alignItems: "center",
      columnGap: "2",
      minWidth: "0",
    },

    itemText: {
      gridColumn: "1",
      gridRow: "1",
      color: `var(${$cardLabelColor})`,
      fontWeight: "semibold",
    },

    // The lock on a locked card. Only locked cards render it.
    itemIndicator: {
      gridColumn: "2",
      gridRow: "1",
      justifySelf: "end",
      display: "inline-flex",
      color: `var(${$cardIndicatorColor})`,
    },

    itemDescription: {
      gridColumn: "1 / -1",
      gridRow: "2",
      color: `var(${$cardDescriptionColor})`,
      // Monospace, so prices and other figures line up from card to card.
      textStyle: "mono.12",
    },

    // Shares the top-right corner with the lock; a card has one or the other.
    itemAddon: {
      gridColumn: "2",
      gridRow: "1",
      justifySelf: "end",
      color: "fg.muted",
      textStyle: "body.12",
    },
  },

  variants: {
    layout: {
      "four-column": {
        root: { gridTemplateColumns: gridColumns(4) },
      },
      "three-column": {
        root: { gridTemplateColumns: gridColumns(3) },
      },
      "two-column": {
        root: { gridTemplateColumns: gridColumns(2) },
      },
    },

    variant: {
      default: {
        item: {
          [$cardBg]: "{colors.bg.container}",
          [$cardStrokeColor]: "{colors.neutral.muted}",
          [$cardStrokeThickness]: "1px",
          [$cardLabelColor]: "{colors.fg.emphasized}",
          [$cardDescriptionColor]: "{colors.fg.emphasized}",
          [$cardIndicatorColor]: "{colors.highlight.solid}",

          _hover: {
            [$cardBg]: "{colors.highlight.subtle}",
            [$cardStrokeColor]: "{colors.highlight.solid}",
            [$cardLabelColor]: "{colors.fg.default}",
          },

          _checked: {
            [$cardBg]: "{colors.highlight.default}",
            [$cardStrokeColor]: "{colors.highlight.solid}",
            [$cardLabelColor]: "{colors.fg.default}",
          },

          _disabled: {
            [$cardBg]: "{colors.neutral.default}",
            [$cardStrokeColor]: "{colors.border.divider}",
            [$cardLabelColor]: "{colors.fg.subtle}",
            [$cardDescriptionColor]: "{colors.fg.subtle}",
            [$cardIndicatorColor]: "{colors.fg.subtle}",
          },
        },
      },

      grayscale: {
        item: {
          [$cardBg]: "{colors.bg.container}",
          [$cardStrokeColor]: "{colors.border.divider}",
          [$cardStrokeThickness]: "2px",
          [$cardLabelColor]: "{colors.fg.default}",
          [$cardDescriptionColor]: "{colors.fg.emphasized}",
          [$cardIndicatorColor]: "{colors.border.inverted}",

          _checked: {
            [$cardStrokeColor]: "{colors.border.inverted}",
          },

          _disabled: {
            [$cardBg]: "{colors.neutral.default}",
            [$cardLabelColor]: "{colors.fg.subtle}",
            [$cardDescriptionColor]: "{colors.fg.subtle}",
            [$cardIndicatorColor]: "{colors.fg.subtle}",
          },
        },
      },
    },

    size: {
      sm: {
        itemContent: {
          rowGap: "0",
        },

        itemControl: {
          px: "2.5",
          py: "2",
        },

        itemText: {
          textStyle: "body.12",
        },

        itemDescription: {
          textStyle: "mono.10",
        },
      },

      md: {
        itemContent: {
          rowGap: "0.5",
        },

        itemText: {
          textStyle: "body.14",
        },
      },

      lg: {
        itemContent: {
          rowGap: "1",
        },

        itemControl: {
          minHeight: "15",
          px: "6",
          py: "4",
        },
      },
    },

    inline: {
      true: {
        root: {
          display: "flex",
          flexWrap: "wrap",
        },

        item: {
          flex: "0 0 auto",
        },

        itemContent: {
          display: "flex",
          alignItems: "center",
          gap: "1",
        },

        // Flex `order` moves the lock, which comes right after the label in
        // the DOM for the grid layout, to the end of the line.
        itemText: { order: "0" },
        itemDescription: { order: "1", fontWeight: "normal" },
        itemIndicator: { order: "2", marginStart: "2" },

        // The single line has no room for an addon.
        itemAddon: { display: "none" },
      },
    },
  },

  defaultVariants: {
    layout: "four-column",
    variant: "default",
    size: "md",
    inline: false,
  },
});
